import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../../config';
import { shouldSkipCachedFeed } from '../../utils/feedFreshness';
import { isNetworkOffline } from '../../utils/requireNetwork';

export const fetchShowcaseLeaderboard = createAsyncThunk(
  'leaderboardShowcase/fetchShowcaseLeaderboard',
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/leaderboard/current-period?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch showcase leaderboard');
      }

      return data;
    } catch (error) {
      return rejectWithValue(`Network error: ${error.message}`);
    }
  },
  {
    condition: ({ page = 1, limit = 10, force = false } = {}, { getState }) => {
      if (force) return true;
      if (isNetworkOffline()) return false;
      const state = getState().leaderboardShowcase;
      return !shouldSkipCachedFeed({
        force,
        fetchedAt: state.lastFetchedAt,
        hasData: state.lastFetchedAt != null,
        page,
        limit,
        cachedPage: state.lastFetchPage,
        cachedLimit: state.lastFetchLimit,
      });
    },
  }
);

const initialState = {
  leaderboard: [],
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  lastFetchPage: 1,
  lastFetchLimit: 10,
  pagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
    totalUsers: 0,
    hasNext: false,
    hasPrev: false
  }
};

const leaderboardShowcaseSlice = createSlice({
  name: 'leaderboardShowcase',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShowcaseLeaderboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShowcaseLeaderboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leaderboard = action.payload.leaderboard || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.error = null;
        const page = action.meta?.arg?.page ?? 1;
        const limit = action.meta?.arg?.limit ?? state.lastFetchLimit;
        if (page === 1) {
          state.lastFetchedAt = Date.now();
          state.lastFetchPage = page;
          state.lastFetchLimit = limit;
        }
      })
      .addCase(fetchShowcaseLeaderboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        if (
          (!action.meta?.arg?.page || action.meta.arg.page === 1) &&
          state.leaderboard.length === 0
        ) {
          state.leaderboard = [];
        }
      });
  },
});

export const { clearError } = leaderboardShowcaseSlice.actions;

export const selectLeaderboardShowcase = (state) => state.leaderboardShowcase;
export const selectShowcaseLeaderboard = (state) => state.leaderboardShowcase.leaderboard;
export const selectLeaderboardShowcaseLoading = (state) => state.leaderboardShowcase.isLoading;
export const selectLeaderboardShowcaseError = (state) => state.leaderboardShowcase.error;
export const selectLeaderboardShowcasePagination = (state) => state.leaderboardShowcase.pagination;

export default leaderboardShowcaseSlice.reducer;
